import React, { useState } from 'react';

import type { ProposedAction } from '../types';
import {
  ActionRow,
  ActionType,
  ApplyButton,
  Card,
  CardButtons,
  CardHeader,
  CardSummary,
  Confidence,
  Description,
  ExpandToggle,
  Payload,
  CardNote,
  Rationale,
  RejectButton,
  ResultBanner,
} from '../styles';

interface Props {
  actions: ProposedAction[];
  // Resolves to whether the turn succeeded; a `void` return counts as success.
  onResolve?: (decision: 'yes' | 'no') => void | Promise<boolean>;
  // Approvals send a bare 'yes'/'no' with no action identity, so an older card's
  // Apply would resolve the newest proposal instead. Until the payload carries
  // actionIds, only the latest card stays actionable.
  readOnly?: boolean;
}

// Renders one card for a whole turn's batch of proposed actions. The approval
// protocol is per-turn, not per-action: a single 'yes'/'no' resolves the entire
// batch, so a bulk request ("create 2 clusters") shows one summary card with a
// single Apply/Reject rather than one card per action.
export function ActionCard({ actions, onResolve, readOnly = false }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [applied, setApplied] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  const [didFail, setDidFail] = useState(false);

  if (actions.length === 0) {
    return null;
  }

  const isBatch = actions.length > 1;

  // The banner claims something about server state, so it waits for the turn.
  const decide = async (decision: 'yes' | 'no') => {
    if (pending) {
      return;
    }
    setPending(true);
    setDidFail(false);
    try {
      const ok = await onResolve?.(decision);
      if (ok === false) {
        setDidFail(true);
        return;
      }
      setApplied(decision === 'yes');
    } finally {
      setPending(false);
    }
  };

  const handleApply = () => decide('yes');
  const handleReject = () => decide('no');

  return (
    <Card>
      {isBatch && <CardSummary>{actions.length} proposed actions</CardSummary>}

      {actions.map((action) => (
        <ActionRow key={action.actionId}>
          <CardHeader>
            <ActionType>{action.actionType}</ActionType>
            {action.confidence && <Confidence>{action.confidence}</Confidence>}
          </CardHeader>

          <Description>{action.description}</Description>
          <Rationale>{action.rationale}</Rationale>

          {expanded && <Payload>{JSON.stringify(action.payload, null, 2)}</Payload>}
        </ActionRow>
      ))}

      <ExpandToggle onClick={() => setExpanded((prev) => !prev)} type="button">
        {expanded ? 'Hide payload' : 'Show payload'}
      </ExpandToggle>

      {applied !== null && (
        <ResultBanner $applied={applied}>{applied ? 'Applied' : 'Rejected'}</ResultBanner>
      )}

      {applied === null && readOnly && !pending && (
        <CardNote>Superseded by a newer proposal.</CardNote>
      )}

      {/* `pending` wins over readOnly: a card the user acted on keeps showing its own
          progress even if a newer proposal lands mid-turn. */}
      {applied === null && (!readOnly || pending) && (
        <CardButtons>
          <ApplyButton disabled={pending} onClick={handleApply} type="button">
            {pending ? 'Applying…' : 'Apply'}
          </ApplyButton>
          <RejectButton disabled={pending} onClick={handleReject} type="button">
            Reject
          </RejectButton>
        </CardButtons>
      )}

      {didFail && <CardNote>That didn&apos;t go through. Please try again.</CardNote>}
    </Card>
  );
}
